import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Trash as TrashIcon,
  ArrowCounterClockwise,
  Warning,
  Buildings,
  ListChecks,
  Clock,
} from "@phosphor-icons/react";
import EmptyState from "@/components/EmptyState";
import ModalPortal from "@/components/ModalPortal";

const TYPE_META = {
  object: { icon: Buildings, color: "text-brand" },
  task: { icon: ListChecks, color: "text-emerald-600" },
  report: { icon: Clock, color: "text-blue-600" },
};

function daysSince(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

function daysUntilPurge(iso) {
  if (!iso) return null;
  const purgeAt = new Date(iso).getTime() + 30 * 24 * 60 * 60 * 1000;
  return Math.max(0, Math.ceil((purgeAt - Date.now()) / (1000 * 60 * 60 * 24)));
}

function relativeTime(iso) {
  if (!iso) return "—";
  const d = daysSince(iso);
  if (d === 0) return "idag";
  if (d === 1) return "för 1 dag sedan";
  return `för ${d} dagar sedan`;
}

export default function Trash() {
  const [data, setData] = useState({ groups: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [confirmPurge, setConfirmPurge] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trash");
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte ladda papperskorgen");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleRestore = async (item) => {
    try {
      await api.post(`/trash/restore/${item.type}/${item.id}`);
      toast.success("Återställd");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte återställa");
    }
  };

  const handlePurge = async (item) => {
    try {
      await api.delete(`/trash/purge/${item.type}/${item.id}`);
      toast.success("Permanent raderad");
      setConfirmPurge(null);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kunde inte radera");
    }
  };

  const orderedTypes = ["object", "task", "report"];

  return (
    <div className="space-y-6" data-testid="trash-page">
      <div>
        <h1 className="font-heading text-3xl text-forest">Papperskorg</h1>
        <p className="text-sm text-text-muted mt-1 max-w-2xl">
          Objekt i papperskorgen raderas automatiskt efter 30 dagar. Endast admin
          kan återställa eller radera slutgiltigt.
        </p>
      </div>

      <div
        data-testid="trash-info-banner"
        className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3 items-start"
      >
        <Warning size={20} className="text-amber-700 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-amber-900 space-y-1">
          <div className="font-medium">Så fungerar papperskorgen</div>
          <ul className="list-disc pl-4 space-y-0.5 text-amber-900/90">
            <li>Raderade objekt, moment och rapporter ligger kvar här i <strong>30 dagar</strong>.</li>
            <li>Du som admin kan <strong>återställa</strong> när som helst under den perioden.</li>
            <li>Efter 30 dagar tas de bort automatiskt och kan inte återskapas.</li>
            <li>Vill du frigöra utrymme direkt? Använd <strong>Radera permanent</strong>.</li>
          </ul>
        </div>
      </div>

      {loading ? (
        <div className="text-text-muted text-sm">Laddar…</div>
      ) : data.total === 0 ? (
        <EmptyState
          testId="trash-empty-state"
          icon={TrashIcon}
          title="Papperskorgen är tom"
          description="Raderade objekt, moment och tidsrapporter dyker upp här och kan återställas i 30 dagar."
        />
      ) : (
        <div className="space-y-8">
          {orderedTypes.map((type) => {
            const group = data.groups[type];
            if (!group || group.count === 0) return null;
            const meta = TYPE_META[type] || {};
            const Icon = meta.icon || TrashIcon;
            return (
              <section key={type} data-testid={`trash-section-${type}`}>
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={20} className={meta.color || "text-text-muted"} />
                  <h2 className="font-heading text-lg text-forest">
                    {group.label}
                  </h2>
                  <span className="text-xs px-2 py-0.5 rounded-full bg-bg-alt text-text-muted">
                    {group.count}
                  </span>
                </div>
                <div className="space-y-2">
                  {group.items.map((item) => {
                    const daysLeft = daysUntilPurge(item.deleted_at);
                    return (
                      <div
                        key={item.id}
                        data-testid={`trash-item-${type}-${item.id}`}
                        className="bg-white border border-border rounded-lg p-4 flex flex-col md:flex-row md:items-center gap-3"
                      >
                        <div className="flex-1 min-w-0">
                          <div
                            className="font-medium text-text truncate"
                            title={item.label}
                          >
                            {item.label}
                          </div>
                          <div className="text-xs text-text-muted mt-0.5 flex flex-wrap gap-x-2">
                            <span>Raderad {relativeTime(item.deleted_at)}</span>
                            {item.deleted_by_name && (
                              <span>· av {item.deleted_by_name}</span>
                            )}
                            {daysLeft !== null && (
                              <span
                                className={`inline-flex items-center gap-1 ${
                                  daysLeft <= 3 ? "text-amber-700" : ""
                                }`}
                              >
                                {daysLeft <= 3 && <Warning size={11} />}
                                · raderas permanent om {daysLeft} dagar
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            data-testid={`trash-restore-${type}-${item.id}`}
                            onClick={() => handleRestore(item)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-forest text-white text-sm hover:bg-forest/90"
                          >
                            <ArrowCounterClockwise size={15} />
                            Återställ
                          </button>
                          <button
                            data-testid={`trash-purge-${type}-${item.id}`}
                            onClick={() => setConfirmPurge(item)}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-red-700 hover:bg-red-50 text-sm"
                          >
                            <TrashIcon size={15} />
                            Radera permanent
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {confirmPurge && (
        <ModalPortal>
        <div
          className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
          data-testid="confirm-purge-modal"
        >
          <div className="bg-white rounded-xl max-w-md w-full p-6 space-y-4">
            <h3 className="font-heading text-xl text-forest">Radera permanent?</h3>
            <p className="text-sm text-text-muted">
              <strong>{confirmPurge.label}</strong> kommer att raderas för alltid
              och kan inte återställas.
            </p>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setConfirmPurge(null)}
                className="px-4 py-2 rounded-lg border border-border hover:bg-bg-alt text-sm"
              >
                Avbryt
              </button>
              <button
                data-testid="confirm-purge-button"
                onClick={() => handlePurge(confirmPurge)}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm hover:bg-red-700"
              >
                Ja, radera permanent
              </button>
            </div>
          </div>
        </div>
        </ModalPortal>
      )}
    </div>
  );
}
